import React, { useEffect, useState } from "react";
import { Box, Typography, Stack, Alert, Button, Chip, Divider, GlobalStyles } from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { OrgInfo } from "../api/OrgApi";
import BarometerApi from "../api/BarometerApi";

// In-app onboarding-kit (org-dashboard → tab "Onboarding-kit").
// Bron: docs/onboarding-kit/ — dit component is de werkgever-gerichte weergave:
// geen interne repo-verwijzingen, en de communicatietemplates worden automatisch
// ingevuld met de organisatienaam, invite-code, fair-use cap en (indien open)
// de barometer-deelnamelink. NL-primair, zoals de bron en de Handleiding-tab.
// Wijzigt het model, werk dan bron én dit component bij (zie kit-README).

const printStyles = (
  <GlobalStyles
    styles={{
      "@media print": {
        "body *": { visibility: "hidden" },
        ".kit-print, .kit-print *": { visibility: "visible" },
        ".kit-print": { position: "absolute", left: 0, top: 0, width: "100%", padding: "24px" },
        ".kit-no-print": { display: "none !important" },
      },
    }}
  />
);

const h2 = { fontSize: "1.15rem", mt: 3, mb: 1 } as const;
const p = { mb: 1.5, color: "text.secondary", lineHeight: 1.7 } as const;

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
      {items.map((it, i) => (
        <Typography key={i} component="li" sx={{ mb: 0.75, color: "text.secondary", lineHeight: 1.7 }}>{it}</Typography>
      ))}
    </Box>
  );
}

// Eén kopieerbare template: nette weergave + knop die de platte tekst kopieert.
function TemplateCard({ title, subject, body }: { title: string; subject?: string; body: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const text = (subject ? `Onderwerp: ${subject}\n\n` : "") + body;
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2500); }
    catch { window.prompt("Kopieer handmatig:", text); }
  };
  return (
    <Box sx={{ p: { xs: 2.5, sm: 3 }, mb: 2.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface-alt)", border: "1px solid var(--border)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
        <Typography variant="h3" sx={{ fontSize: "1.02rem", m: 0, flex: 1, minWidth: 200 }}>{title}</Typography>
        <Button size="small" variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={copy} className="kit-no-print">
          {copied ? "Gekopieerd ✓" : "Kopieer tekst"}
        </Button>
      </Box>
      {subject && <Typography sx={{ fontSize: ".9rem", mb: 1 }}><strong>Onderwerp:</strong> {subject}</Typography>}
      <Typography component="pre" sx={{ m: 0, whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: ".92rem", color: "text.secondary", lineHeight: 1.65 }}>{body}</Typography>
    </Box>
  );
}

const DOCS = ["comms", "cpbw", "dpo", "prijs"] as const;
type DocKey = (typeof DOCS)[number];
const DOC_LABELS: Record<DocKey, string> = {
  comms: "Interne communicatie",
  cpbw: "CPBW-briefing",
  dpo: "DPO-one-pager",
  prijs: "Prijsblad",
};

export default function OnboardingKit({ org }: { org: OrgInfo }) {
  const [doc, setDoc] = useState<DocKey>("comms");
  const [barometerLink, setBarometerLink] = useState("");

  // Vul [BAROMETER-LINK] automatisch met de open golf, als die er is.
  useEffect(() => {
    BarometerApi.listWaves().then((waves) => {
      const open = waves.find((w) => w.status === "open");
      if (open) setBarometerLink(`${window.location.origin}/barometer/${open.shareToken}`);
    }).catch(() => {});
  }, []);

  const ORG = org.name;
  const CODE = org.inviteCode;
  const CAP = org.fairUseCapPerMember;
  const BARO = barometerLink || "[BAROMETER-LINK — kopieer de deelnamelink uit de tab Welzijnsbarometer]";

  const commsNode = (
    <>
      <Typography sx={p}>
        Kant-en-klare teksten voor jullie interne communicatie, automatisch ingevuld met jullie
        organisatienaam, invite-code en fair-use cap. Pas de tone-of-voice gerust aan, maar neem de
        <strong> privacy-passages en crisisverwijzingen letterlijk over</strong> — die zijn afgestemd op onze
        gepubliceerde privacystandaard. Vervang zelf nog <code>[NAAM / HR]</code> en, waar van toepassing, de datum.
      </Typography>

      <TemplateCard title="1. Aankondigingsmail aan alle werknemers"
        subject="Nieuw: gratis psychologische ondersteuning via Carefunded"
        body={`Beste collega,

Vanaf vandaag kan iedereen bij ${ORG} gebruikmaken van Carefunded: een Belgisch online platform dat je in enkele stappen matcht met een erkende klinisch psycholoog, voor coaching of therapie via een beveiligd videogesprek — 's avonds en overdag, zonder wachtlijst. Ook zelfhulptrajecten (slaap, stress, piekeren, …) zijn inbegrepen.

Wat betekent dit concreet?

- ${ORG} betaalt tot ${CAP} sessies per jaar volledig voor jou. Je betaalt zelf niets en hoeft niets voor te schieten.
- Aanmelden doe je zelf, rechtstreeks bij Carefunded: ga naar carefunded.be, maak een account en voer de code ${CODE} in (tabblad "Organisatie" in je dashboard → "Aansluiten").

En je privacy? Dit is volledig vertrouwelijk. ${ORG} ziet niet wie zich aanmeldt, wie sessies volgt of waarover die gaan — wij ontvangen alleen anonieme totaalcijfers over groepen van minstens 10 personen. De psycholoog werkt onder beroepsgeheim en Carefunded bewaart geen gespreksinhoud. Lees er alles over op carefunded.be/privacystandaard.

Zit je ergens mee, groot of klein? Dit is er voor jou.

Met vriendelijke groet,
[NAAM / HR]

Carefunded is geen nooddienst. Heb je nu hulp nodig? Bel de Zelfmoordlijn op 1813 (gratis, 24/7) of 112 bij acuut gevaar.`} />

      <TemplateCard title="2. Intranet-tekst"
        body={`Praten helpt — Carefunded voor alle medewerkers

Iedereen zit weleens minder goed in zijn vel. Via Carefunded kan je vertrouwelijk en online bij een erkende klinisch psycholoog terecht, betaald door ${ORG} (tot ${CAP} sessies per jaar). Geen wachtlijst, geen verwijsbrief, geen papierwerk.

Zo werkt het

1. Ga naar carefunded.be en maak een persoonlijk account aan.
2. Open in je dashboard het tabblad "Organisatie" en voer de code ${CODE} in.
3. Doorloop de korte intake en boek meteen een (gratis) kennismakingssessie.

100% vertrouwelijk. ${ORG} ziet nooit wie het platform gebruikt — enkel anonieme groepscijfers (minimaal 10 personen). Je gesprekken worden niet opgenomen en er worden geen verslagen bij Carefunded bewaard. Meer uitleg: carefunded.be/privacystandaard.

Carefunded is geen nooddienst — bel 1813 (Zelfmoordlijn) of 112 bij acute nood.`} />

      <TemplateCard title="3. FAQ voor werknemers"
        body={`Ziet mijn werkgever dat ik sessies volg?
Nee, nooit. Je werkgever ziet niet wie zich aanmeldt, wie een intake doet of wie sessies volgt — ook niet achteraf, ook niet op de factuur. ${ORG} ontvangt uitsluitend anonieme totaalcijfers (bijvoorbeeld "aantal sessies deze maand"), en alleen over groepen van minstens 10 personen. Onder die drempel wordt zelfs geen aantal getoond. Dit is technisch zo ingebouwd én contractueel vastgelegd.

Ziet mijn werkgever mijn antwoorden op de welzijnsbevraging (barometer)?
Nee. De barometer is volledig anoniem: iedereen krijgt dezelfde link, er wordt geen naam, account of IP-adres geregistreerd, en je werkgever ziet alleen groepsgemiddelden vanaf 10 deelnemers.

Wie ziet wat ik met mijn psycholoog bespreek?
Alleen jij en je psycholoog. De psycholoog is gebonden aan het beroepsgeheim. Videogesprekken worden niet opgenomen en Carefunded bewaart geen sessienotities.

Wat kost het mij?
Niets, tot ${CAP} sessies per jaar — ${ORG} betaalt die volledig. Wil je daarna verder, dan kan dat op eigen kosten; je betaalt dan rechtstreeks (vaak met gedeeltelijke terugbetaling via je mutualiteit) en je werkgever wordt daar op geen enkele manier bij betrokken.

Moet ik dit met mijn leidinggevende of HR bespreken?
Nee. Je meldt je rechtstreeks bij Carefunded aan met de organisatiecode. Er is geen goedkeuring nodig — van niemand.

Is dit iets voor "zware" problemen?
Nee — je kan er ook terecht voor stress, slaap, piekeren, een moeilijke periode of gewoon een klankbord. Vroeg praten werkt het best.

Wat als ik in crisis zit?
Carefunded is geen nooddienst. Bel dan de Zelfmoordlijn op 1813 (gratis, 24/7), chat via zelfmoord1813.be, bel Tele-Onthaal op 106, of 112 bij acuut gevaar.

Waar vind ik meer over privacy?
carefunded.be/privacystandaard en carefunded.be/privacy. Vragen: privacy@carefunded.be.`} />

      <TemplateCard title="4. Reminder (na 4-6 weken)"
        subject="Al aan jezelf gedacht? Carefunded staat voor je klaar"
        body={`Beste collega,

Een tijdje geleden lanceerden we Carefunded: vertrouwelijke gesprekken met een erkende psycholoog, volledig betaald door ${ORG}. Veel collega's vonden de weg al — volledig anoniem, dus je merkt er niets van, en dat is precies de bedoeling.

Ook iets voor jou? Slecht slapen, piekeren, stress of gewoon nood aan een goed gesprek: je hoeft niet te wachten tot het "erg genoeg" is.

Aanmelden: carefunded.be → account aanmaken → code ${CODE} → intake → boeken. De kennismakingssessie is gratis en vrijblijvend.

Je werkgever ziet nooit wie deelneemt — enkel anonieme groepscijfers (min. 10 personen).

[NAAM / HR]

Geen nooddienst — bel 1813 of 112 bij acute nood.`} />

      <TemplateCard title="5. Barometer-uitnodiging (per golf te versturen)"
        subject={`3 minuten voor je welzijn — anonieme welzijnsbarometer ${ORG}`}
        body={`Beste collega,

Hoe gaat het écht met ons als organisatie? Om daar zicht op te krijgen — en er iets aan te kunnen doen — organiseren we samen met Carefunded een korte, volledig anonieme welzijnsbevraging. Ze bestaat uit wetenschappelijk gevalideerde vragen over welzijn en werkbeleving en duurt ongeveer 3 minuten.

Deelnemen: ${BARO} — open tot [DATUM].

Volledig anoniem, zo gebouwd:

- Iedereen krijgt exact dezelfde link — er is geen login en geen koppeling met je account.
- Er wordt geen naam, e-mailadres of IP-adres geregistreerd.
- ${ORG} ziet uitsluitend groepsgemiddelden en enkel als minstens 10 collega's deelnamen — anders wordt er niets getoond, zelfs niet hoeveel mensen meededen.
- Niemand ziet je individuele antwoorden — ook Carefunded kan ze niet aan jou koppelen.

Hoe meer collega's deelnemen, hoe betrouwbaarder het beeld — en hoe gerichter er aan werkpunten gewerkt kan worden, samen met onze preventieadviseur.

Alvast bedankt!
[NAAM / HR]

De bevraging geeft geen persoonlijke score of diagnose. Zit je ergens mee? Via Carefunded kan je vertrouwelijk met een psycholoog praten (code ${CODE}). In nood: 1813 of 112.`} />
    </>
  );

  const cpbwNode = (
    <>
      <Typography sx={p}>
        Deze briefing is bedoeld om <strong>door u voor te leggen</strong> aan het Comité voor Preventie en
        Bescherming op het Werk (CPBW), de ondernemingsraad en uw preventieadviseur psychosociale aspecten.
        Druk af of bewaar als PDF en voeg ze bij de agenda.
      </Typography>

      <Typography variant="h2" sx={h2}>1. Wat is Carefunded?</Typography>
      <Typography sx={p}>
        Carefunded is een Belgisch online platform voor geestelijke gezondheid. Werknemers worden na een korte
        intake gematcht met een <strong>erkende klinisch psycholoog</strong> voor coaching, therapie of begeleide
        zelfhulp, via versleutelde videogesprekken in de browser. Daarnaast biedt het platform zelfhulptrajecten aan.
      </Typography>
      <Bullets items={[
        <><strong>Carefunded is geen zorgaanbieder.</strong> De zorg wordt verleend door zelfstandige, erkende klinisch psychologen, gebonden aan het beroepsgeheim. Carefunded verzorgt de matching, planning, videoverbinding en betaling.</>,
        <><strong>Carefunded bewaart geen klinische inhoud.</strong> Geen sessienotities, geen opnames van gesprekken. Het klinisch dossier ligt bij de psycholoog, onder beroepsgeheim.</>,
        <><strong>Er zit geen AI in het product.</strong> Matching gebeurt via vaste, gepubliceerde regels (carefunded.be/hoe-matchen-wij).</>,
        <><strong>Carefunded is geen nooddienst.</strong> Crisiskanalen (112, Zelfmoordlijn 1813, Tele-Onthaal 106) worden overal op het platform getoond.</>,
      ]} />

      <Typography variant="h2" sx={h2}>2. Hoe werkt het voor uw werknemers?</Typography>
      <Bullets items={[
        <>De werkgever ontvangt een <strong>invite-code</strong>. Werknemers registreren zelf op carefunded.be en voeren die code in. <strong>De werkgever ziet niet wie zich aansluit.</strong></>,
        <>De werknemer doorloopt de intake en boekt rechtstreeks bij een psycholoog. Sessies binnen de afgesproken fair-use cap worden door de werkgever gefinancierd; de werknemer betaalt daarvoor niets.</>,
        <>Boven de cap schakelt het platform <strong>automatisch en onzichtbaar voor de werkgever</strong> over op zelf betalen (met mutualiteits-terugbetaling). Er is <strong>geen goedkeuringsstap bij HR</strong> — bewust, zodat niemand bij de werkgever ooit individueel zorggebruik te zien krijgt.</>,
      ]} />

      <Typography variant="h2" sx={h2}>3. Wat de werkgever wél ziet — en wat nooit</Typography>
      <Typography variant="h3" sx={{ fontSize: "1rem", mt: 1.5, mb: 0.5 }}>Wél (uitsluitend geaggregeerd en anoniem)</Typography>
      <Bullets items={[
        <>Totale aantallen gefinancierde sessies per <strong>maand en per type</strong> (therapie/coaching) — dezelfde granulariteit als op de factuur; geen datums, geen tijdstippen.</>,
        <>De resultaten van de <strong>anonieme organisatie-barometer</strong> (§5), uitsluitend als groepsgemiddelden vanaf <strong>10 respondenten</strong>.</>,
        <>Kostentotalen en het verbruik van een eventuele gepoolde extra-sessie-pot ("pool: X van Y gebruikt" — zonder namen).</>,
      ]} />
      <Typography variant="h3" sx={{ fontSize: "1rem", mt: 1.5, mb: 0.5 }}>Nooit</Typography>
      <Bullets items={[
        <><strong>Wie</strong> zich heeft aangesloten, een intake deed, sessies volgt of een zelftest invulde.</>,
        <>Individuele scores, antwoorden of gezondheidsgegevens — van niemand, in geen enkel scherm, export of factuur.</>,
        <>Groepscijfers over <strong>minder dan 10 personen</strong>: die worden volledig onderdrukt, inclusief het aantal. Kleine groepen zijn ook niet te reconstrueren door views van elkaar af te trekken (complementaire cellen worden mee onderdrukt) — er zijn geen vrije filters of tijdssnedes.</>,
        <>Meldingen over individueel gedrag. <strong>Alerting bestaat alleen op geaggregeerde kostendrempels</strong> (bv. "80% van het afgesproken budget bereikt"), nooit op verbruikspieken van personen of kleine teams.</>,
      ]} />
      <Typography sx={p}>
        Deze anonimiteits-firewall is contractueel verankerd (her-identificatieverbod in de B2B-overeenkomst) en
        technisch afgedwongen (k≥10-suppressie in de software). De volledige gepubliceerde standaard staat op
        carefunded.be/privacystandaard.
      </Typography>

      <Typography variant="h2" sx={h2}>4. Verhouding tot de wettelijke psychosociale risicoanalyse</Typography>
      <Bullets items={[
        <><strong>Carefunded ondersteunt de verplichte psychosociale risicoanalyse a priori (Welzijnswet 4 augustus 1996, hfdst. Vbis; Codex Boek I, Titel 3); het vervangt ze niet.</strong> Uw (interne of externe) preventieadviseur psychosociale aspecten blijft de wettelijke actor: advies vóór maatregelen, betrokkenheid van het CPBW en effectieve preventiemaatregelen blijven uw verantwoordelijkheid.</>,
        <>De barometer-output is <strong>input voor uw preventieadviseur of externe dienst</strong> — géén risicoanalyse op zich. Deelname is vrijwillig; de respons is dus een zelfgeselecteerde steekproef en methodologisch niet gelijkwaardig aan het erkende instrumentarium van uw externe dienst.</>,
        <>Wij claimen nergens dat het gebruik van Carefunded u "compliant maakt".</>,
      ]} />

      <Typography variant="h2" sx={h2}>5. De anonieme organisatie-barometer</Typography>
      <Typography sx={p}>
        Periodiek (vaste golven) ontvangt het volledige personeelsbestand via uw interne communicatie
        <strong> één gedeelde, anonieme link</strong> naar een korte vragenlijst. U kiest per golf welke onderdelen
        meegaan: de wetenschappelijk gevalideerde Cantril-ladder (welzijn) en Burnout Assessment Tool (BAT-12,
        KU Leuven), en/of een korte set eigen Carefunded-werkbelevingsvragen (transparant gelabeld: eigen vragen,
        géén gevalideerd klinisch instrument of klinische screening).
      </Typography>
      <Bullets items={[
        <><strong>Geen login.</strong> De link is voor iedereen dezelfde; er is geen koppeling met een account.</>,
        <>Er wordt <strong>geen userId, IP-adres of browser-fingerprint</strong> opgeslagen, en geen fijnmazige tijdstippen (enkel de dag), zodat ook timing-correlatie niet mogelijk is.</>,
        <>De deelnemer krijgt na afloop een <strong>uniforme wegwijzer</strong> naar het hulpaanbod — geen score, geen "ernst"-label (bewuste keuze onder de regelgeving voor medische hulpmiddelen).</>,
        <>U ziet per golf uitsluitend groepsgemiddelden vanaf <strong>10 respondenten</strong>; daaronder wordt niets getoond, ook niet hoeveel mensen deelnamen. Er zijn geen team- of afdelingscohorten.</>,
      ]} />
      <Typography sx={p}>
        Omdat de inzameling écht anoniem is, verwerkt de barometer geen persoonsgegevens. De onderbouwing is
        gedocumenteerd en kan uw DPO inkijken (zie de DPO-one-pager in deze kit).
      </Typography>

      <Typography variant="h2" sx={h2}>6. Waarom uw advies gevraagd wordt</Typography>
      <Typography sx={p}>
        Wanneer u Carefunded kadert in uw <strong>preventiebeleid</strong> (bv. als welzijnsvoordeel dat mee invulling
        geeft aan psychosociaal welzijn op het werk, of wanneer barometer-resultaten worden voorgelegd als input voor
        de risicoanalyse), hoort het <strong>CPBW voorafgaand advies</strong> te geven (Codex Boek I, Titel 3 en
        Boek II, Titel 7). Ook zonder die formele kadering vinden wij werknemersinspraak vóór de uitrol de juiste
        volgorde. Concreet vragen we het Comité zich uit te spreken over:
      </Typography>
      <Bullets items={[
        "de invoering van Carefunded als door de werkgever gefinancierd hulpaanbod;",
        "de frequentie en interne communicatie van de barometer-golven;",
        "de afspraak dat resultaten uitsluitend via de preventieadviseur richting risicoanalyse/maatregelen stromen.",
      ]} />

      <Typography variant="h2" sx={h2}>7. Vragen</Typography>
      <Bullets items={[
        "Inhoudelijk/organisatorisch: uw contactpersoon bij Carefunded, of support@carefunded.be",
        "Privacy: privacy@carefunded.be — DPO: Mr. Franklin BV (olivier@misterfranklin.be)",
        "Publieke documentatie: carefunded.be/privacystandaard · carefunded.be/hoe-matchen-wij · carefunded.be/privacy",
      ]} />
    </>
  );

  const dpoNode = (
    <>
      <Typography sx={p}>
        Eén pagina voor uw DPO, jurist of privacy-verantwoordelijke: datastromen, rollen, grondslagen en waarborgen.
        Druk af of bewaar als PDF en stuur door.
      </Typography>
      <Bullets items={[
        <><strong>Dienst:</strong> Carefunded (carefunded.be) — online matching met erkende klinisch psychologen + versleutelde videosessies + zelfhulp, deels gefinancierd door de werkgever (B2B).</>,
        <><strong>Verwerkingsverantwoordelijke platform:</strong> Praxie Group BV, KBO 0474.564.877, Doornelei 17 bus 103, 2018 Antwerpen.</>,
        <><strong>DPO:</strong> Mr. Franklin BV — Olivier Sustronck, olivier@misterfranklin.be · privacy@carefunded.be.</>,
      ]} />

      <Typography variant="h2" sx={h2}>Rollen</Typography>
      <Bullets items={[
        <><strong>Praxie Group BV (Carefunded)</strong> is verwerkingsverantwoordelijke voor account, intake, matching, boeking, betaling, cursusgebruik en de B2B-aggregaten.</>,
        <><strong>De psycholoog</strong> is een zelfstandige verwerkingsverantwoordelijke voor de zorg zelf (beroepsgeheim; klinisch dossier off-platform). Carefunded bewaart <strong>geen klinische inhoud</strong>: geen sessienotities, geen video-opnames.</>,
        <><strong>De werkgever</strong> wordt géén verwerker of (mede)verantwoordelijke voor gezondheidsgegevens: hij ontvangt uitsluitend <strong>anonieme aggregaten</strong> en heeft geen toegang tot individuele records. Ledenbeheer verloopt via een invite-code die de werknemer zelf invoert; de werkgever ziet niet wie zich aansluit.</>,
      ]} />

      <Typography variant="h2" sx={h2}>Datastromen (kern)</Typography>
      <Bullets items={[
        <><strong>Werknemer ↔ platform, rechtstreeks.</strong> De werknemer registreert zelf, doorloopt de intake en boekt zelf. Er loopt géén werknemersdata via de werkgever, en géén zorggebruiksdata naar de werkgever. Er zijn geen HR-touchpoints per werknemer; ook boven de fair-use cap is er geen goedkeuringsstap (automatisch zelf-betalen).</>,
        <><strong>Werkgever ← platform: uitsluitend aggregaten.</strong> Sessie-aantallen per maand × type (identiek aan de factuurgranulariteit; geen datums), barometer-groepsresultaten en kostentotalen. Drempel <strong>k≥10 met volledige suppressie</strong>: onder de 10 wordt niets getoond, ook geen aantallen; complementaire cellen worden mee onderdrukt; geen vrije filters of tijdssnedes.</>,
        <><strong>Anonieme barometer.</strong> Eén gedeelde link per golf naar het hele personeelsbestand, zonder login; geen userId/IP/user-agent, timestamps enkel op dag-niveau, vaste golven, geen cohorten. Anoniem bij inzameling → geen persoonsgegevens (uitgebreide onderbouwing op verzoek via privacy@carefunded.be).</>,
      ]} />

      <Typography variant="h2" sx={h2}>Grondslagen (samengevat)</Typography>
      <Bullets items={[
        <>Platformdienst (account, matching, boeking, betaling) — <strong>art. 6(1)(b) AVG</strong>, overeenkomst met de werknemer.</>,
        <>Gezondheidsgegevens intake/matchprofiel — <strong>art. 9(2)(a) AVG</strong>, expliciete toestemming van de werknemer, intrekbaar via carefunded.be/mijn-gegevens; <strong>niet</strong> de arbeidsrelatie als kapstok.</>,
        <>Zorgverlening door de psycholoog — <strong>art. 9(2)(h) AVG</strong>, gezondheidszorg door beroepsbeoefenaar onder beroepsgeheim.</>,
        <>Facturatie/boekhouding — <strong>art. 6(1)(c) AVG</strong>, wettelijke verplichting.</>,
        <>B2B-aggregaten & barometer-output — <strong>anonieme gegevens</strong> (overweging 26 AVG): k≥10 + suppressie + geen her-identificatiemiddelen.</>,
      ]} />

      <Typography variant="h2" sx={h2}>Waarborgen</Typography>
      <Bullets items={[
        <><strong>Geen doorgifte van individueel zorggebruik</strong> aan de werkgever — technisch afgedwongen (k≥10-suppressie in code, alerting alleen op geaggregeerde kostendrempels) én contractueel: de B2B-overeenkomst bevat een <strong>her-identificatieverbod</strong> (schending = wezenlijke tekortkoming).</>,
        <><strong>EU-hosting en -verwerking</strong>: Microsoft Azure (EU), MongoDB Atlas (EU-regio), video via Daily.co met EU-geo-pinning; betalingen via Stripe. Actuele lijst + doorgiftemechanismen: carefunded.be/sub-verwerkers.</>,
        <><strong>Geen AI</strong> in het product; geen tracking/analytics van derden; minimale functionele cookies.</>,
        <>Beveiliging (art. 32): encryptie in transit/at rest, RBAC, afgedwongen MFA voor psychologen/beheerders, rate limiting, CSP, audit-logging (365 d), geteste restore, DPIA aanwezig.</>,
        <>Retentie: intake-vrije-tekst max. 30 dagen; facturen 7 jaar; accountdata tot verwijdering (self-service via carefunded.be/mijn-gegevens, incl. export en consent-intrekking).</>,
      ]} />

      <Typography variant="h2" sx={h2}>Wat wij van u vragen</Typography>
      <Typography sx={p}>
        Niets bijzonders: uw organisatie ontvangt geen persoonsgegevens en hoeft dus geen verwerkersovereenkomst met
        ons te sluiten voor dit dossier. Wij vragen wél het her-identificatieverbod in de B2B-overeenkomst te
        aanvaarden en de interne communicatie (tab "Interne communicatie" in deze kit) te gebruiken zodat werknemers
        correct geïnformeerd zijn. Verdere documentatie: carefunded.be/privacystandaard · carefunded.be/privacy ·
        carefunded.be/sub-verwerkers · DPIA-inzage en barometer-annex op verzoek via privacy@carefunded.be.
      </Typography>
    </>
  );

  const prijsNode = (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>Richtprijzen, alle bedragen excl. BTW tenzij anders vermeld — het ondertekende voorstel primeert.</Alert>

      <Typography variant="h2" sx={h2}>Het model in één alinea</Typography>
      <Typography sx={p}>
        Geen prepaid pot, geen bedrag per werknemer vooraf. U betaalt <strong>maandelijks het werkelijke
        sessieverbruik</strong> van uw medewerkers (sessietarief van de psycholoog + een kleine vaste
        servicevergoeding per sessie) en <strong>één vaste jaarfee</strong> voor het platform. Een fair-use cap per
        werknemer begrenst uw kost; daarboven betaalt de medewerker automatisch zelf verder — zonder dat u daar ooit
        bij betrokken wordt of ziet wie het betreft.
      </Typography>

      <Typography variant="h2" sx={h2}>Per sessie (bij werkgeversfinanciering)</Typography>
      <Bullets items={[
        <>Sessietarief van de psycholoog (getoond vóór boeking) + vaste Carefunded-fee aan de werkgeverskant: <strong>€5</strong>/sessie therapie, <strong>€2</strong>/sessie coaching.</>,
        <>Fees zijn <strong>vaste bedragen, nooit percentages</strong> (deontologisch vereist: het honorarium van de psycholoog staat los van onze vergoeding). De kennismakingssessie is gratis.</>,
        <>U betaalt enkel sessies die <strong>werkelijk plaatsvonden</strong>.</>,
      ]} />

      <Typography variant="h2" sx={h2}>Vaste jaarfee (per organisatie, op headcount)</Typography>
      <Bullets items={[
        <>Minder dan 100 werknemers: <strong>€990</strong> · 100 tot 500: <strong>€1.990</strong> · meer dan 500: <strong>€4.990</strong>.</>,
        <><strong>Inbegrepen:</strong> werkgeversdashboard (k≥10-standaard), de anonieme organisatie-barometer met groepsrapport per golf, periodieke rapportage (aantallen × type × maand + kwartaalrapport) als input voor uw preventieadviseur en CPBW, onboarding-support (invite-codes, CPBW-briefing, DPO-one-pager, interne communicatie — deze kit), onbeperkt aantal aangesloten werknemers en toegang tot de zelfhulptrajecten.</>,
      ]} />

      <Typography variant="h2" sx={h2}>Fair-use cap — en wat erboven gebeurt</Typography>
      <Bullets items={[
        <>U kiest een <strong>cap per werknemer per kalenderjaar</strong> (standaard <strong>6</strong> sessies, aanpasbaar). Tot de cap financiert u; dat is uw maximale exposure per medewerker.</>,
        <><strong>Boven de cap schakelt het platform automatisch over op zelf betalen</strong> door de medewerker, met mutualiteits-terugbetaling waar van toepassing. Er is <strong>geen goedkeuringsstap bij HR</strong>.</>,
        <>Méér dekken kan anonimiteits-veilig: de cap <strong>collectief</strong> verhogen, of een <strong>gepoolde extra-sessie-pot</strong> financieren die Carefunded naamloos toekent (u ziet enkel "pool: X van Y gebruikt").</>,
        <>Budgetbewaking: instelbare <strong>alerts op kostendrempels</strong> — altijd op totaalniveau, nooit per persoon of klein team.</>,
      ]} />

      <Typography variant="h2" sx={h2}>Facturatie</Typography>
      <Bullets items={[
        <><strong>Maandelijks:</strong> één factuur met (a) de doorstorting van de sessietarieven — geïnd <em>in naam en voor rekening van de behandelende psychologen</em> (verzamelstaat; de psycholoog is de zorgverstrekker) — en (b) de Carefunded-sessievergoedingen (€5/€2, +21% BTW). Op de factuur staan <strong>aantallen × type × maand</strong> — geen datums of namen.</>,
        <><strong>Jaarlijks:</strong> de vaste jaarfee (+21% BTW), bij de start van elk contractjaar.</>,
        <><strong>Betalingstermijn: 30 dagen.</strong> Bij wanbetaling pauzeert de werkgeversfinanciering; uw medewerkers vallen neutraal terug op zelf betalen (mét mutualiteits-terugbetaling), zonder enige zichtbaarheid voor collega's. Na betaling wordt de financiering weer geactiveerd.</>,
        <>Elektronische facturatie (Peppol BIS) voor BTW-plichtige werkgevers. Therapie met therapeutisch doel is BTW-vrijgesteld (art. 44 WBTW); coaching is belast — de BTW-behandeling per sessie volgt de kwalificatie door de psycholoog.</>,
      ]} />

      <Typography variant="h2" sx={h2}>Rekenvoorbeeld (indicatief)</Typography>
      <Typography sx={p}>
        Organisatie van 250 werknemers, cap 6 sessies, sessietarief €60, 10% van de medewerkers gebruikt gemiddeld
        5 therapiesessies: 125 sessies/jaar × (€60 + €5) = <strong>€8.125/jaar</strong> (≈ €677/maand, volgt het
        werkelijke gebruik) + jaarfee <strong>€1.990</strong> ≈ <strong>€10.115/jaar</strong> ≈ <strong>€40 per
        werknemer</strong> — u betaalde enkel voor werkelijk gebruik.
      </Typography>

      <Typography variant="h2" sx={h2}>Wat u nooit betaalt</Typography>
      <Typography sx={p}>
        Geen kost per aangesloten werknemer, geen setup-fee, geen kost voor niet-gebruikte sessies, geen meerprijs
        voor de barometer of rapportage. Vragen of een voorstel op maat: support@carefunded.be.
      </Typography>
    </>
  );

  const content: Record<DocKey, React.ReactNode> = { comms: commsNode, cpbw: cpbwNode, dpo: dpoNode, prijs: prijsNode };

  return (
    <Box sx={{ maxWidth: 900 }}>
      {printStyles}
      <Typography sx={{ ...p, mb: 2 }} className="kit-no-print">
        Alles wat u nodig hebt om Carefunded intern te lanceren: kant-en-klare communicatie (automatisch ingevuld
        met uw gegevens), een briefing voor uw Comité PBW, een one-pager voor uw DPO en het prijsblad.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 1.5 }} className="kit-no-print">
        {DOCS.map((d) => (
          <Chip key={d} label={DOC_LABELS[d]} onClick={() => setDoc(d)}
            variant={doc === d ? "filled" : "outlined"} color={doc === d ? "primary" : "default"} />
        ))}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<PrintRoundedIcon />} onClick={() => window.print()}>
          Afdrukken of bewaren als PDF
        </Button>
      </Stack>
      <Divider sx={{ mb: 1 }} className="kit-no-print" />

      <Box className="kit-print">
        <Typography variant="h2" sx={{ fontSize: "1.3rem", mt: 1.5, mb: 1 }}>{DOC_LABELS[doc]}</Typography>
        {content[doc]}
      </Box>
    </Box>
  );
}
